/**
 * @name umi 的路由配置
 * @description 只支持 path,component,routes,redirect,wrappers,name,icon 的配置
 * @param path  path 只支持两种占位符配置，第一种是动态参数 :id 的形式，第二种是 * 通配符，通配符只能出现路由字符串的最后。
 * @param component 配置 location 和 path 匹配后用于渲染的 React 组件路径。可以是绝对路径，也可以是相对路径，如果是相对路径，会从 src/pages 开始找起。
 * @param routes 配置子路由，通常在需要为多个路径增加 layout 组件时使用。
 * @param redirect 配置路由跳转
 * @param wrappers 配置路由组件的包装组件，通过包装组件可以为当前的路由组件组合进更多的功能。 比如，可以用于路由级别的权限校验
 * @param name 配置路由的标题，默认读取国际化文件 menu.ts 中 menu.xxxx 的值，如配置 name 为 login，则读取 menu.ts 中 menu.login 的取值作为标题
 * @param icon 配置路由的图标，取值参考 https://ant.design/components/icon-cn， 注意去除风格后缀和大小写，如想要配置图标为 <StepBackwardOutlined /> 则取值应为 stepBackward 或 StepBackward，如想要配置图标为 <UserOutlined /> 则取值应为 user 或者 User
 * @doc https://umijs.org/docs/guides/routes
 */
export default [
  {
    path: '/',
    component: './SmartRedirect',
  },
  {
    name: 'work-items',
    path: '/work-items',
    component: './WorkItems',
    hideInMenu: true,
  },
  {
    path: '/user',
    layout: false,
    routes: [
      {
        name: 'login',
        path: '/user/login',
        component: './User/Login',
      },
    ],
  },
  {
    path: '/admin',
    name: 'admin',
    icon: 'crown',
    access: 'isAdmin',
    routes: [
      {
        path: '/admin',
        redirect: '/admin/sub-page',
      },
      {
        path: '/admin/sub-page',
        name: 'sub-page',
        component: './Admin',
      },
    ],
  },

  {
    name: 'product-management',
    icon: 'shopping',
    path: '/product-management',
    access: 'productModule',
    routes: [
      {
        path: '/product-management',
        component: './ProductManagement/ModuleRedirect',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'products',
        icon: 'shopping',
        path: '/product-management/products',
        access: 'canViewProducts',
        routes: [
          {
            path: '/product-management/products',
            component: './ProductManagement',
            exact: true,
          },
          {
            path: '/product-management/products/:id',
            component: './ProductDetail',
            hideInMenu: true,
          },
          {
            path: '/product-management/products-debug',
            component: './ProductManagement/debug',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'category-review',
        icon: 'audit',
        path: '/product-management/category-review',
        component: './CategoryManagement/CategoryReview',
        access: 'canViewCategoryReview',
      },
      {
        name: 'categories',
        icon: 'appstore',
        path: '/product-management/categories',
        component: './CategoryManagement',
        access: 'canViewCategories',
      },
      {
        name: 'brands',
        icon: 'tags',
        path: '/product-management/brands',
        component: './BrandManagement',
        access: 'canViewBrands',
      },
      {
        name: 'discounts',
        icon: 'gift',
        path: '/product-management/discounts',
        component: './DiscountManagement',
        access: 'canViewDiscount',
      },
      {
        name: 'no-barcode-products',
        icon: 'qrcode',
        path: '/product-management/no-barcode-products',
        component: './NoBarcodeProductManagement',
        access: 'canViewNoBarcodeProducts',
      },
      {
        name: 'price-change-management',
        icon: 'dollar',
        path: '/product-management/price-change-management',
        component: './PriceChangeManagement',
        access: 'price_change:view',
      },
      {
        name: 'hanshow-electronic-shelf-labels',
        icon: 'barcode',
        path: '/product-management/hanshow-electronic-shelf-labels',
        component: './HanshowElectronicShelfLabels',
        access: 'canViewHanshowElectronicShelfLabels',
      },
      {
        name: 'product-change-logs',
        icon: 'history',
        path: '/product-management/product-change-logs',
        access: 'canViewProductChangeLogs',
        routes: [
          {
            path: '/product-management/product-change-logs',
            component: './ProductChangeLogs',
            exact: true,
          },
          {
            path: '/product-management/product-change-logs/:id',
            component: './ProductChangeLogs/Detail',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'expired-product-management',
        icon: 'clock-circle',
        path: '/product-management/expired-product-management',
        component: './ExpiredProductManagement',
        access: 'expired_product_management:view',
      },
      {
        name: 'unsalable-product-report',
        icon: 'alert',
        path: '/product-management/unsalable-product-report',
        component: './UnsalableProductReportManagement',
        access: 'expired_product_management:unsalable_report_view',
      },
      {
        name: 'product-expiry-date-errors',
        icon: 'calendar',
        path: '/product-management/product-expiry-date-errors',
        component: './DataAnomaly/ProductExpiryDateErrors',
        access: 'canViewProductExpiryDateErrors',
      },
      {
        name: 'supply-products',
        icon: 'safety',
        path: '/product-management/supply-products',
        component: './SupplyProductManagement',
        access: 'canViewSupplyProducts',
      },
      {
        name: 'featured-products',
        icon: 'star',
        path: '/product-management/featured-products',
        component: './FeaturedProductManagement/index',
        access: 'canViewFeaturedProducts',
      },
      {
        name: 'banned-products',
        icon: 'stop',
        path: '/product-management/banned-products',
        component: './BannedProductManagement/index',
        access: 'canViewBannedProducts',
      },
      {
        name: 'product-stock-type',
        icon: 'tags',
        path: '/product-management/product-stock-type',
        component: './DataAnomaly/ProductStockTypeManagement/index',
        access: 'canManageProductStockType',
      },
      {
        name: 'carryover-product-management',
        icon: 'pie-chart',
        path: '/product-management/carryover-product-management',
        component: './CarryoverProductManagement',
        access: 'canViewCarryoverProductManagement',
      },
      {
        name: 'shipment-specs',
        icon: 'container',
        path: '/product-management/shipment-specs',
        component: './ShipmentSpecManagement',
        access: 'canViewShipmentSpec',
      },
      {
        name: 'product-displays',
        icon: 'appstore',
        path: '/product-management/product-displays',
        component: './ProductDisplayManagement',
        access: 'canViewProductDisplays',
      },
      {
        name: 'product-descriptions',
        icon: 'file-text',
        path: '/product-management/product-descriptions',
        component: './ProductDescriptionManagement',
        access: 'canViewProductDescriptionManagement',
      },
    ],
  },
  {
    name: 'orders',
    icon: 'fileText',
    path: '/orders',
    access: 'canViewOrders',
    routes: [
      {
        path: '/orders',
        redirect: '/orders/list',
      },
      {
        name: 'list',
        path: '/orders/list',
        component: './OrderManagement',
        access: 'canViewOrderList',
      },
      {
        name: 'wholesale',
        path: '/orders/wholesale',
        component: './WholesaleOrderManagement',
        access: 'canViewWholesaleOrders',
      },
      {
        name: 'employee-purchase-refund',
        icon: 'audit',
        path: '/orders/employee-purchase-refund',
        component: './PurchaseOrder/EmployeePurchaseRefund',
        access: 'canViewEmployeePurchaseRefund',
        exact: true,
      },
      {
        path: '/orders/employee-purchase-refund/review/:id',
        component: './PurchaseOrder/EmployeePurchaseRefund',
        access: 'canViewEmployeePurchaseRefund',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'reconciliation',
        path: '/orders/reconciliation',
        component: './OrderManagement/Reconciliation',
        access: 'canViewOrderReconciliation',
      },
    ],
  },
  {
    name: 'purchase-order-management',
    icon: 'shoppingCart',
    path: '/purchase-order-management',
    access: 'purchaseModule',
    routes: [
      {
        path: '/purchase-order-management',
        component: './PurchaseOrder/ModuleRedirect',
        hideInMenu: true,
        exact: true,
      },
      {
        path: '/purchase-order-management/command-center',
        redirect: '/supplier-management/follow-up',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'procurement-goals',
        icon: 'aim',
        path: '/purchase-order-management/goals',
        component: './ProcurementGoals',
        access: 'canViewProcurementGoals',
        exact: true,
      },
      {
        path: '/purchase-order-management/goals/:id',
        component: './ProcurementGoals',
        access: 'canViewProcurementGoals',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'opportunities',
        icon: 'bulb',
        path: '/purchase-order-management/opportunities',
        access: 'canViewPurchaseOpportunity',
        hideInMenu: true,
        routes: [
          {
            path: '/purchase-order-management/opportunities',
            component: './PurchaseOpportunity',
            exact: true,
          },
          {
            path: '/purchase-order-management/opportunities/:id',
            component: './PurchaseOpportunity/Detail',
            hideInMenu: true,
            exact: true,
          },
          {
            path: '/purchase-order-management/opportunities/:id/edit',
            component: './PurchaseOpportunity/Edit',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'offers',
        icon: 'tags',
        path: '/purchase-order-management/offers',
        access: 'canViewPurchaseQuote',
        routes: [
          {
            path: '/purchase-order-management/offers',
            component: './PurchaseOffer',
            exact: true,
          },
          {
            path: '/purchase-order-management/offers/new',
            component: './PurchaseOffer/Create',
            hideInMenu: true,
          },
          {
            path: '/purchase-order-management/offers/:id',
            component: './PurchaseOffer/Detail',
            hideInMenu: true,
            exact: true,
          },
          {
            path: '/purchase-order-management/offers/:id/edit',
            component: './PurchaseOffer/Edit',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'ai-purchase-import',
        icon: 'robot',
        path: '/purchase-order-management/ai-purchase-import',
        access: 'canViewAiPurchaseImport',
        routes: [
          {
            path: '/purchase-order-management/ai-purchase-import',
            component: './AiPurchaseImport',
            exact: true,
          },
          {
            path: '/purchase-order-management/ai-purchase-import/draft-offers/:id/review',
            component: './AiPurchaseImport/Review',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'pendingApprovalProducts',
        icon: 'audit',
        path: '/purchase-order-management/pending-approval-products',
        component: './PurchaseOffer/PendingApprovalProducts',
        access: 'canApprovePurchaseQuote',
        exact: true,
      },
      {
        name: 'submittedProducts',
        icon: 'unorderedList',
        path: '/purchase-order-management/submitted-products',
        component: './PurchaseOffer/SubmittedProducts',
        access: 'canViewPurchaseQuote',
        exact: true,
      },
      {
        name: 'orders',
        icon: 'fileText',
        path: '/purchase-order-management/orders',
        access: 'canViewPurchaseOrder',
        routes: [
          {
            path: '/purchase-order-management/orders',
            component: './PurchaseOrder',
            exact: true,
          },
          {
            path: '/purchase-order-management/orders/new',
            component: './PurchaseOrder/Create',
            hideInMenu: true,
          },
          {
            path: '/purchase-order-management/orders/create-from-offer/:offerId',
            component: './PurchaseOrder/CreateFromOffer',
            hideInMenu: true,
          },
          {
            path: '/purchase-order-management/orders/:id/request-modification',
            component: './PurchaseOrder/RequestModification',
            hideInMenu: true,
          },
          {
            path: '/purchase-order-management/orders/:id',
            component: './PurchaseOrder/Detail',
            hideInMenu: true,
          },
          {
            path: '/purchase-order-management/orders/:id/edit',
            component: './PurchaseOrder/Edit',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'market-price-search',
        icon: 'search',
        path: '/purchase-order-management/market-price-search',
        component: './PurchaseOrder/MarketPriceSearch',
        access: 'canViewMarketPriceSearch',
        exact: true,
      },
      {
        name: 'greenprice-catalog',
        icon: 'picture',
        path: '/purchase-order-management/greenprice-catalog',
        component: './PurchaseOrder/GreenpriceCatalog',
        access: 'canViewGreenpriceCatalog',
        exact: true,
      },
      {
        name: 'greenprice-whatsapp',
        icon: 'message',
        path: '/purchase-order-management/greenprice-whatsapp',
        component: './PurchaseOrder/GreenpriceWhatsapp',
        access: 'canViewGreenpriceCatalog',
        exact: true,
      },
      {
        name: 'price-competitiveness',
        icon: 'lineChart',
        path: '/purchase-order-management/price-competitiveness',
        component: './PurchaseOrder/PriceCompetitiveness',
        access: 'canViewPriceCompetitiveness',
        exact: true,
      },
      {
        name: 'payment-management',
        icon: 'creditCard',
        path: '/purchase-order-management/payment-management',
        component: './PurchaseOrder',
        access: 'canViewPurchaseOrderPayment',
        exact: true,
      },
      {
        name: 'payment-vouchers',
        icon: 'download',
        path: '/purchase-order-management/payment-vouchers',
        component: './PurchaseOrder/PaymentVoucherCenter',
        access: 'canViewPurchaseOrderPaymentVoucher',
        exact: true,
      },
      {
        name: 'finance-management',
        icon: 'fund',
        path: '/purchase-order-management/finance-management',
        component: './PurchaseOrder/FinanceManagement',
        access: 'canViewPurchaseOrderFinanceManagement',
        exact: true,
      },
      {
        name: 'logistics-fee-management',
        icon: 'car',
        path: '/purchase-order-management/logistics-fee-management',
        component: './PurchaseOrder/LogisticsFeeManagement',
        access: 'canViewLogisticsFeeManagement',
        exact: true,
      },

      {
        name: 'order-payment-followup',
        icon: 'alert',
        path: '/purchase-order-management/order-payment-followup',
        component: './PurchaseOrder/HandlingFollowup',
        access: 'canViewPurchaseOrderHandlingFollowup',
        exact: true,
      },
      {
        name: 'modification-approval',
        icon: 'audit',
        path: '/purchase-order-management/modification-approval',
        component: './PurchaseOrder/ModificationApproval',
        access: 'canViewPurchaseOrderModifyApproval',
        exact: true,
      },
      {
        name: 'arrival-schedule',
        icon: 'calendar',
        path: '/purchase-order-management/arrival-schedule',
        component: './PurchaseOrder/ArrivalSchedule',
        access: 'canViewPurchaseOrderArrivalSchedule',
        exact: true,
      },
      {
        name: 'category-arrival-calendar',
        icon: 'calendar',
        path: '/purchase-order-management/category-arrival-calendar',
        component: './InventoryManagement/CategoryArrivalCalendar',
        access: 'canViewPurchaseCategoryArrivalCalendar',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'payment-variance-review',
        icon: 'audit',
        path: '/purchase-order-management/payment-variance-review',
        component: './PurchaseOrder/PaymentVarianceReview',
        access: 'canViewPurchaseOrderPaymentVarianceReview',
        exact: true,
      },
    ],
  },
  {
    name: 'procurement-navigation-mock',
    path: '/procurement-navigation-mock',
    component: './ProcurementNavigationMock',
    hideInMenu: true,
    exact: true,
  },
  {
    name: 'supplier-management',
    icon: 'shop',
    path: '/supplier-management',
    access: 'supplierModule',
    routes: [
      {
        path: '/supplier-management',
        redirect: '/supplier-management/suppliers',
      },
      {
        name: 'supplier-follow-up',
        icon: 'fieldTime',
        path: '/supplier-management/follow-up',
        component: './SupplierFollowUp',
        access: 'canViewSupplierFollowUp',
      },
      {
        name: 'supplier-development',
        icon: 'global',
        path: '/supplier-management/supplier-development',
        component: './SupplierDevelopment',
        access: 'canViewSupplierDevelopment',
        exact: true,
      },
      {
        name: 'suppliers',
        icon: 'team',
        path: '/supplier-management/suppliers',
        component: './SupplierManagement',
        access: 'canViewSuppliers',
      },
      {
        name: 'supplier-groups',
        icon: 'apartment',
        path: '/supplier-management/supplier-groups',
        component: './SupplierGroupMatching',
        access: 'canViewSupplierPlatformGroups',
      },
      {
        path: '/supplier-management/supplier-messages',
        redirect: '/supplier-management/supplier-groups',
        hideInMenu: true,
      },
      {
        name: 'supplier-calls',
        icon: 'phone',
        path: '/supplier-management/calls',
        component: './SupplierCalls',
        access: 'canViewSupplierCalls',
        exact: true,
      },
    ],
  },
  {
    path: '/brands',
    redirect: '/product-management/brands',
    hideInMenu: true,
    access: 'canViewBrands',
  },
  {
    // Keep old bookmarks and URL-based table filters working after the menu move.
    path: '/category-management',
    component: './ProductManagement/LegacyCategoryRedirect',
    access: 'productModule',
    hideInMenu: true,
    exact: true,
  },
  {
    path: '/category-management/categories',
    component: './ProductManagement/LegacyCategoryRedirect',
    access: 'canViewCategories',
    hideInMenu: true,
    exact: true,
  },
  {
    path: '/category-management/category-review',
    component: './ProductManagement/LegacyCategoryRedirect',
    access: 'canViewCategoryReview',
    hideInMenu: true,
    exact: true,
  },
  {
    name: 'store-management',
    icon: 'home',
    path: '/store-management',
    access: 'storeModule',
    routes: [
      {
        path: '/store-management',
        redirect: '/store-management/locations',
      },
      {
        name: 'locations',
        icon: 'environment',
        path: '/store-management/locations',
        component: './LocationManagement',
        access: 'canViewStores',
      },
      {
        name: 'location-rent',
        icon: 'bank',
        path: '/store-management/location-rent',
        component: './Finance/Rent',
        access: 'canViewStoreRent',
      },
      {
        name: 'store-targets',
        icon: 'pieChart',
        path: '/store-management/store-targets',
        component: './StoreTargets',
        access: 'canViewStoreTargets',
      },
      {
        name: 'category-placement',
        icon: 'swap',
        path: '/store-management/category-placement',
        component: './CategoryPlacementManagement',
        access: 'canViewCategoryPlacement',
      },
      {
        name: 'pda-operation-logs',
        icon: 'audit',
        path: '/store-management/pda-operation-logs',
        component: './PdaOperationLogs',
        access: 'canViewPdaOperationLogs',
      },
      {
        name: 'pda-accounts',
        icon: 'tablet',
        path: '/store-management/pda-accounts',
        component: './PdaAccountManagement',
        access: 'canViewPdaAccount',
      },
    ],
  },
  {
    name: 'inventory-management',
    icon: 'database',
    path: '/inventory-management',
    access: 'inventoryModule',
    routes: [
      {
        path: '/inventory-management',
        redirect: '/inventory-management/overview',
      },
      {
        name: 'inventory-overview',
        icon: 'table',
        path: '/inventory-management/overview',
        component: './InventoryManagement',
        access: 'canViewInventoryOverview',
      },
      {
        name: 'inventory-history',
        icon: 'history',
        path: '/inventory-management/history',
        component: './InventoryHistory',
        access: 'canViewInventoryHistory',
      },
      {
        name: 'inventory-transactions',
        icon: 'transaction',
        path: '/inventory-management/transactions',
        component: './InventoryTransactions',
        access: 'canViewInventoryChangeLog',
      },
      {
        name: 'inventory-task-queue',
        icon: 'unordered-list',
        path: '/inventory-management/task-queue',
        component: './InventoryTaskQueue',
        access: 'canViewInventoryTaskQueue',
      },
      {
        name: 'inventory-check-followups',
        icon: 'issuesClose',
        path: '/inventory-management/inventory-check-followups',
        component: './InventoryManagement/InventoryCheckFollowUps',
        access: 'canViewInventoryCheckFollowUps',
      },
      {
        name: 'inventory-loss',
        icon: 'alert',
        path: '/inventory-management/loss',
        component: './InventoryLossManagement',
        access: 'canViewInventoryLoss',
      },
      {
        name: 'product-damage-reports',
        icon: 'warning',
        path: '/inventory-management/product-damage-reports',
        component: './ProductDamageReportManagement',
        access: 'canViewProductDamageReports',
      },
      {
        name: 'stock-take',
        icon: 'reconciliation',
        path: '/inventory-management/stock-take',
        component: './StockTakeManagement',
        access: 'canViewStocktake',
      },
      {
        name: 'financial-loss-dashboard',
        icon: 'fund',
        path: '/inventory-management/financial-loss-dashboard',
        component: './InventoryFinancialLossDashboard',
        access: 'canViewInventoryFinancialLossDashboard',
      },
    ],
  },
  {
    name: 'script-management',
    icon: 'code',
    path: '/script-management',
    access: 'scriptModule',
    routes: [
      {
        path: '/script-management',
        redirect: '/script-management/scripts',
      },
      {
        name: 'scripts',
        path: '/script-management/scripts',
        component: './Script',
        access: 'canViewScripts',
      },
      {
        name: 'script-runs',
        path: '/script-management/script-runs',
        component: './ScriptRunRecords',
        access: 'canViewScriptExecutionLog',
      },
    ],
  },
  {
    path: '/data-anomaly/product-expiry-date-errors',
    component: './ProductManagement/LegacyExpiryDateErrorRedirect',
    access: 'canViewProductExpiryDateErrors',
    hideInMenu: true,
  },
  {
    name: 'data-anomaly',
    icon: 'warning',
    path: '/data-anomaly',
    access: 'dataAnomalyModule',
    routes: [
      {
        path: '/data-anomaly',
        redirect: '/data-anomaly/bad-product-names',
      },
      {
        name: 'bad-product-names',
        path: '/data-anomaly/bad-product-names',
        component: './DataAnomaly/BadProductNames',
        access: 'canViewBadProductNames',
      },
      {
        name: 'category-errors',
        path: '/data-anomaly/category-errors',
        component: './DataAnomaly/CategoryErrors',
        access: 'canViewCategoryErrors',
      },
      {
        name: 'missing-categories',
        path: '/data-anomaly/missing-categories',
        component: './DataAnomaly/MissingCategories',
        access: 'canViewMissingCategories',
      },
      {
        name: 'missing-brand',
        path: '/data-anomaly/missing-brand',
        component: './DataAnomaly/MissingBrand',
        access: 'canViewMissingBrand',
      },
      {
        name: 'missing-description',
        path: '/data-anomaly/missing-description',
        component: './DataAnomaly/MissingDescription',
        access: 'canViewMissingDescription',
      },
      {
        name: 'duplicate-products',
        path: '/data-anomaly/duplicate-products',
        component: './DataAnomaly/DuplicateProducts',
        access: 'canManageDuplicateProducts',
      },

      {
        name: 'duplicate-barcodes',
        path: '/data-anomaly/duplicate-barcodes',
        component: './DuplicateBarcodeManagement',
        access: 'canViewDuplicateBarcode',
      },
      {
        name: 'data-checks',
        path: '/data-anomaly/data-checks',
        component: './DataAnomaly/DataChecks',
        access: 'canViewDataChecks',
      },
    ],
  },
  {
    name: 'procurement-analysis',
    icon: 'barChart',
    path: '/procurement-analysis',
    access: 'procurementAnalyticsModule',
    routes: [
      {
        path: '/procurement-analysis',
        redirect: '/procurement-analysis/dashboard',
      },
      {
        name: 'procurement-dashboard',
        icon: 'dashboard',
        path: '/procurement-analysis/dashboard',
        component: './Analytics/ProcurementDashboard',
        access: 'canViewProcurementDashboard',
      },
      {
        name: 'purchase-analysis',
        icon: 'barChart',
        path: '/procurement-analysis/purchase-analysis',
        component: './Analytics/PurchaseAnalysis',
        access: 'canViewPurchaseAnalysis',
      },
      {
        name: 'purchase-history-performance',
        icon: 'table',
        path: '/procurement-analysis/purchase-history-performance',
        component: './Analytics/PurchaseHistoryPerformance',
        access: 'canViewPurchaseHistoryPerformance',
      },
      {
        name: 'purchase-data-errors',
        icon: 'warning',
        path: '/procurement-analysis/purchase-data-errors',
        component: './Analytics/PurchaseDataErrors',
        access: 'canViewPurchaseDataErrors',
      },
      {
        name: 'small-trial-sale-followup',
        icon: 'experiment',
        path: '/procurement-analysis/small-trial-sale-followup',
        component: './Analytics/SmallTrialSaleFollowup',
        access: 'canViewSmallTrialSaleFollowup',
      },
      {
        name: 'opportunity-analytics',
        icon: 'lineChart',
        path: '/procurement-analysis/opportunity-analytics',
        component: './PurchaseOpportunityAnalytics',
        access: 'canViewPurchaseOpportunityAnalytics',
      },
      {
        name: 'replenishment',
        icon: 'sync',
        path: '/procurement-analysis/replenishment',
        component: './ReplenishmentManagement',
        access: 'canViewReplenishment',
      },
      {
        name: 'category-arrival-calendar',
        icon: 'calendar',
        path: '/procurement-analysis/category-arrival-calendar',
        component: './InventoryManagement/CategoryArrivalCalendar',
        access: 'canViewPurchaseCategoryArrivalCalendar',
        exact: true,
      },
    ],
  },
  {
    name: 'sales-analysis',
    icon: 'barChart',
    path: '/sales-analysis',
    access: 'salesAnalysisModule',
    routes: [
      {
        path: '/sales-analysis',
        redirect: '/sales-analysis/dashboard',
      },
      {
        name: 'dashboard',
        icon: 'dashboard',
        path: '/sales-analysis/dashboard',
        component: './Analytics',
        access: 'canViewDataDashboard',
      },
      {
        name: 'product-sales-analysis',
        icon: 'barChart',
        path: '/sales-analysis/product-sales',
        component: './Analytics/ProductSalesAnalysis',
        access: 'canViewProductSalesAnalysis',
      },
      {
        name: 'product-specific-sales',
        icon: 'table',
        path: '/sales-analysis/product-specific-sales',
        component: './Analytics/ProductSpecificSales',
        access: 'canViewProductSpecificSales',
      },
      {
        name: 'category-sales-analysis',
        icon: 'percentageOutlined',
        path: '/sales-analysis/category-sales',
        component: './Analytics/CategorySalesAnalysis',
        access: 'canViewCategorySales',
      },
      {
        name: 'payment-gateway-analysis',
        icon: 'creditCard',
        path: '/sales-analysis/payment-gateway',
        component: './Analytics/PaymentGatewayAnalysis',
        access: 'canViewPaymentGatewayAnalysis',
      },
      {
        name: 'category-sales-charts',
        icon: 'lineChart',
        path: '/sales-analysis/category-sales-charts',
        component: './Analytics/CategorySalesCharts',
        access: 'canViewCategorySalesCharts',
      },
      {
        name: 'supplier-sales-report',
        icon: 'solution',
        path: '/sales-analysis/supplier-sales-report',
        component: './Analytics/SupplierSalesReport',
        access: 'canViewSupplierSalesReport',
      },
      {
        name: 'franchise-store-sales-analysis',
        icon: 'shop',
        path: '/sales-analysis/franchise-store-sales-analysis',
        component: './Analytics/FranchiseStoreSalesAnalysis',
        access: 'canViewFranchiseStoreSalesAnalysis',
      },
    ],
  },
  {
    name: 'inventory-analysis',
    icon: 'pieChart',
    path: '/inventory-analysis',
    access: 'inventory_analysis',
    routes: [
      {
        path: '/inventory-analysis',
        redirect: '/inventory-analysis/warehouse-structure',
      },
      {
        name: 'warehouse-structure',
        icon: 'cluster',
        path: '/inventory-analysis/warehouse-structure',
        component: './InventoryManagement/WarehouseStructureAnalysis',
        access: 'warehouse_structure_analysis:view',
      },
      {
        name: 'store-structure',
        icon: 'shop',
        path: '/inventory-analysis/store-structure',
        component: './InventoryManagement/StoreStructureAnalysis',
        access: 'store_structure_analysis:view',
      },
      {
        name: 'store-inventory-analysis',
        icon: 'shop',
        path: '/inventory-analysis/store-inventory',
        component: './InventoryManagement/StoreInventoryAnalysis',
        access: 'inventory_store_analysis:view',
      },
      {
        name: 'turnover-analysis',
        icon: 'fieldTime',
        path: '/inventory-analysis/turnover-analysis',
        component: './InventoryManagement/TurnoverAnalysis',
        access: 'inventory_turnover_analysis:view',
      },
      {
        path: '/inventory-analysis/arrival-calendar',
        redirect: '/warehouse-transfer/arrival-schedule',
        hideInMenu: true,
      },
      {
        name: 'expiry-analysis',
        icon: 'clockCircle',
        path: '/inventory-analysis/expiry-analysis',
        component: './InventoryManagement/ExpiryAnalysis',
        access: 'inventory_expiry_analysis:view',
      },
      {
        name: 'unsold-products-analysis',
        icon: 'inbox',
        path: '/inventory-analysis/unsold-products',
        component: './InventoryManagement/UnsoldProductsAnalysis',
        access: 'inventory_unsold_products:view',
      },
    ],
  },
  {
    name: 'pos-system-management',
    icon: 'creditCard',
    path: '/pos-system-management',
    access: 'posModule',
    routes: [
      {
        path: '/pos-system-management',
        component: './PosSystemManagement/ModuleRedirect',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'location-accounts',
        icon: 'key',
        path: '/pos-system-management/location-accounts',
        component: './LocationAccountManagement',
        access: 'canViewStoreAccount',
      },
      {
        name: 'payment-gateways',
        icon: 'creditCard',
        path: '/pos-system-management/payment-gateways',
        component: './PaymentGatewayManagement',
        access: 'canViewPaymentMethod',
      },
      {
        name: 'pos-app-versions',
        icon: 'mobile',
        path: '/pos-system-management/pos-app-versions',
        component: './PosAppVersionManagement',
        access: 'canViewPosAppVersion',
      },
      {
        name: 'pos-sessions',
        icon: 'solution',
        path: '/pos-system-management/pos-sessions',
        component: './PosSessionManagement',
        access: 'canViewPosSession',
      },
      {
        name: 'operation-logs',
        icon: 'history',
        path: '/pos-system-management/operation-logs',
        component: './PosOperationLogs',
        access: 'canViewPosOperationLogs',
      },
      {
        name: 'receipt-content',
        icon: 'fileText',
        path: '/pos-system-management/receipt-content',
        component: './PosReceiptContentManagement',
        access: 'canViewPosReceiptContent',
      },
      {
        name: 'quick-actions',
        icon: 'appstore',
        path: '/pos-system-management/quick-actions',
        component: './PosQuickActionManagement',
        access: 'canViewPosQuickActions',
      },
      {
        name: 'bea-config-management',
        icon: 'setting',
        path: '/pos-system-management/bea-config-management',
        component: './BEAConfigManagement',
        access: 'canViewBeaConfig',
      },
    ],
  },
  {
    name: 'online-store-management',
    icon: 'shop',
    path: '/online-store-management',
    access: 'onlineStoreModule',
    routes: [
      {
        path: '/online-store-management',
        component: './OnlineStoreManagement/ModuleRedirect',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'online-products',
        icon: 'shop',
        path: '/online-store-management/products',
        component: './OnlineProductManagement',
        access: 'canViewOnlineProducts',
      },
      {
        name: 'online-orders',
        icon: 'fileText',
        path: '/online-store-management/orders',
        component: './OnlineOrderManagement',
        access: 'canViewOnlineOrders',
      },
      {
        name: 'online-order-followup',
        icon: 'audit',
        path: '/online-store-management/order-followup',
        component: './OnlineOrderFollowup',
        access: 'canViewOnlineOrderFollowup',
      },
      {
        name: 'online-store-behaviour',
        icon: 'lineChart',
        path: '/online-store-management/analytics',
        component: './Analytics/OnlineStoreBehaviour',
        access: 'canViewOnlineStoreBehaviour',
      },
      {
        name: 'online-store-discount-codes',
        icon: 'gift',
        path: '/online-store-management/discount-codes',
        component: './OnlineStoreDiscountCodeManagement',
        access: 'canViewOnlineStoreDiscountCodes',
      },
      {
        name: 'online-store-customer-messages',
        icon: 'message',
        path: '/online-store-management/customer-messages',
        component: './OnlineStoreCustomerMessages',
        access: 'canViewOnlineStoreCustomerMessages',
      },

      {
        name: 'online-store-blog-posts',
        icon: 'read',
        path: '/online-store-management/blog-posts',
        component: './OnlineStoreBlogPosts',
        access: 'canViewOnlineStoreBlogPosts',
      },
      {
        name: 'online-store-email-notification-tasks',
        icon: 'mail',
        path: '/online-store-management/email-notification-tasks',
        component: './OnlineStoreEmailNotificationTasks',
        access: 'canViewOnlineStoreEmailNotifications',
      },
    ],
  },
  {
    name: 'marketing-management',
    icon: 'lineChart',
    path: '/marketing-management',
    access: 'canViewMarketingManagement',
    routes: [
      {
        path: '/marketing-management',
        redirect: '/marketing-management/dashboard',
      },
      {
        name: 'dashboard',
        icon: 'dashboard',
        path: '/marketing-management/dashboard',
        component: './MarketingManagement',
        access: 'canViewMarketingManagement',
      },
    ],
  },
  {
    name: 'store-operations-management',
    icon: 'schedule',
    path: '/store-operations-management',
    access: 'storeOperationsModule',
    routes: [
      {
        path: '/store-operations-management',
        redirect: '/store-operations-management/store-tasks',
      },
      {
        name: 'store-tasks',
        icon: 'schedule',
        path: '/store-operations-management/store-tasks',
        component: './StoreOperations/StoreTasks',
        access: 'store_tasks_management:view',
      },
      {
        path: '/store-operations-management/store-tasks/:id',
        component: './StoreOperations/StoreTasks/Detail',
        hideInMenu: true,
      },
      {
        path: '/store-operations-management/store-tasks/:id/edit',
        component: './StoreOperations/StoreTasks/Edit',
        hideInMenu: true,
      },
      {
        name: 'support-tickets',
        icon: 'customerService',
        path: '/store-operations-management/support-tickets',
        component: './StoreOperations/SupportTickets',
        access: 'store_support_tickets:view',
      },
      {
        name: 'roaster-records',
        icon: 'qrcode',
        path: '/store-operations-management/roaster-records',
        component: './StoreOperations/RoasterRecords',
        access: 'roaster_records:view',
      },
      {
        name: 'store-tutorials',
        icon: 'read',
        path: '/store-operations-management/store-tutorials',
        component: './StoreOperations/StoreTutorials',
        access: 'store_tutorials:view',
      },
      {
        name: 'store-support-contacts',
        icon: 'contacts',
        path: '/store-operations-management/store-support-contacts',
        component: './StoreOperations/SupportContacts',
        access: 'canViewStoreSupportContacts',
      },
      {
        name: 'pda-daily-inventory-check',
        icon: 'checkSquare',
        path: '/store-operations-management/pda-daily-inventory-check',
        component: './StoreOperations/PdaDailyInventoryCheck',
        access: 'pda_daily_inventory_check:view',
      },
      {
        name: 'store-inspection-question-management',
        icon: 'fileText',
        path: '/store-operations-management/store-inspection-question-management',
        component: './StoreOperations/StoreInspectionQuestionManagement',
        access: 'store_inspection_question_management:view',
      },
      {
        name: 'store-inspection-reports',
        icon: 'solution',
        path: '/store-operations-management/store-inspection-reports',
        component: './StoreOperations/StoreInspectionReports',
        access: 'store_inspection_reports:view',
      },
      {
        name: 'store-incentive',
        icon: 'fundProjectionScreen',
        path: '/store-operations-management/store-incentive',
        component: './StoreOperations/StoreIncentive',
        access: 'store_bonus:view',
      },
    ],
  },
  {
    name: 'market-info',
    icon: 'lineChart',
    path: '/market-info',
    access: 'marketInfoModule',
    routes: [
      {
        path: '/market-info',
        redirect: '/market-info/collection-tasks',
      },
      {
        name: 'opportunities',
        icon: 'bulb',
        path: '/market-info/opportunities',
        access: 'canViewPurchaseOpportunity',
        redirect: '/purchase-order-management/opportunities',
      },
      {
        name: 'collection-tasks',
        icon: 'schedule',
        path: '/market-info/collection-tasks',
        access: 'canViewCollectionTask',
        routes: [
          {
            path: '/market-info/collection-tasks',
            component: './MarketInfo/CollectionTasks',
          },
          {
            path: '/market-info/collection-tasks/:id/image-import',
            component: './MarketInfo/ImageImport',
            hideInMenu: true,
          },
          {
            path: '/market-info/collection-tasks/:id',
            component: './MarketInfo/CollectionTasks/TaskDetail',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'products',
        icon: 'shoppingCart',
        path: '/market-info/products',
        access: 'canViewMiProduct',
        routes: [
          {
            path: '/market-info/products',
            component: './MarketInfo/Products',
          },
          {
            path: '/market-info/products/:id',
            component: './MarketInfo/Products/Detail',
            hideInMenu: true,
            exact: true,
          },
          {
            path: '/market-info/products/:id/edit',
            component: './MarketInfo/Products/Edit',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'data-review',
        icon: 'audit',
        path: '/market-info/data-review',
        component: './MarketInfo/DataReview',
        access: 'canViewDataReview',
      },
      {
        name: 'data-review-detail',
        hideInMenu: true,
        path: '/market-info/data-review/:id',
        component: './MarketInfo/DataReviewDetail',
      },
      {
        name: 'analysis',
        icon: 'barChart',
        path: '/market-info/analysis',
        component: './MarketInfo/Analysis',
        access: 'canViewDataAnalysis',
      },
      {
        name: 'price-analysis',
        icon: 'dollarCircle',
        path: '/market-info/price-analysis',
        component: './MarketInfo/PriceAnalysis',
        access: 'canViewPriceAnalysis',
      },
      {
        name: 'market-product-analysis',
        icon: 'pieChart',
        path: '/market-info/market-product-analysis',
        component: './MarketInfo/MarketProductAnalysis',
        access: 'canViewMarketProductAnalysis',
      },
      {
        name: 'mi-users',
        icon: 'userAdd',
        path: '/market-info/mi-users',
        component: './MarketInfo/MiUsers',
        access: 'canViewMiUser',
      },
      {
        name: 'convert-to-opportunities',
        icon: 'swap',
        path: '/market-info/convert-to-opportunities',
        component: './MarketInfo/ConvertToOpportunities',
        access: 'canConvertToOpportunity',
      },
    ],
  },
  {
    name: 'hr-management',
    icon: 'team',
    path: '/hr-management',
    access: 'hrManagementModule',
    routes: [
      {
        path: '/hr-management',
        component: './HrManagement/ModuleRedirect',
        hideInMenu: true,
        exact: true,
      },
      {
        name: 'dashboard',
        icon: 'dashboard',
        path: '/hr-management/dashboard',
        component: './dashboard/hrDashboard/page',
        access: 'canViewHrDashboard',
        exact: true,
      },
      {
        name: 'employees',
        icon: 'team',
        path: '/hr-management/employees',
        component: './dashboard/employees/page',
        access: 'canViewHrEmployees',
        exact: true,
      },
      {
        path: '/hr-management/employees/:id',
        component: './dashboard/employees/detail',
        access: 'canViewHrEmployees',
        hideInMenu: true,
      },
      {
        name: 'roster-schedule',
        icon: 'schedule',
        path: '/hr-management/roster-schedule',
        component: './dashboard/rosterSchedule/page',
        access: 'canViewHrRoster',
      },
      {
        name: 'attendance-stats',
        icon: 'barChart',
        path: '/hr-management/attendance-stats',
        component: './dashboard/attendanceStats/page',
        access: 'canViewHrAttendanceStats',
      },
      {
        name: 'work-hour-anomalies',
        icon: 'fieldTime',
        path: '/hr-management/work-hour-anomalies',
        component: './dashboard/workHourAnomalies/page',
        access: 'canViewHrWorkHourAnomalies',
      },
      {
        name: 'leave-management',
        icon: 'calendar',
        path: '/hr-management/leave-management',
        component: './dashboard/leaveManagement/page',
        access: 'canViewHrLeave',
      },
      {
        name: 'salaries',
        icon: 'dollarCircle',
        path: '/hr-management/salaries',
        component: './dashboard/salaries/page',
        access: 'canViewHrSalaries',
      },
      {
        name: 'salary-visibility-management',
        icon: 'userSwitch',
        path: '/hr-management/salary-visibility-management',
        component: './dashboard/salaryVisibility/page',
        access: 'canViewHrSalaryVisibility',
      },
      {
        name: 'change-logs',
        icon: 'history',
        path: '/hr-management/change-logs',
        component: './dashboard/hrChangeLogs/page',
        access: 'canViewHrAuditLogs',
      },
    ],
  },
  {
    path: '/inventory-management/anomaly-dashboard',
    component: './InventoryAnomalyDashboard',
    access: 'canViewInventoryAnomalyDashboard',
    hideInMenu: true,
  },
  {
    name: 'warehouse-transfer',
    icon: 'swap',
    path: '/warehouse-transfer',
    access: 'warehouseTransferModule',
    routes: [
      {
        path: '/warehouse-transfer',
        redirect: '/warehouse-transfer/list',
      },
      {
        name: 'warehouse-transfer-dashboard',
        icon: 'dashboard',
        path: '/warehouse-transfer/dashboard',
        component: './WarehouseTransferManagement/Dashboard',
        access: 'canViewWarehouseDashboard',
      },
      {
        name: 'warehouse-transfer-list',
        path: '/warehouse-transfer/list',
        component: './WarehouseTransferManagement',
        access: 'canViewTransferOrder',
      },
      {
        name: 'pda-operation-logs',
        icon: 'history',
        path: '/warehouse-transfer/pda-operation-logs',
        component: './WarehouseTransferManagement/PdaOperationLogs',
        access: 'canViewPdaShippingLogs',
      },
      {
        path: '/warehouse-transfer/inventory-anomaly-dashboard',
        redirect: '/inventory-management/anomaly-dashboard',
        access: 'canViewInventoryAnomalyDashboard',
        hideInMenu: true,
      },
      {
        name: 'warehouse-transfer-product-records',
        path: '/warehouse-transfer/product-records',
        component: './WarehouseTransferManagement/ProductTransferRecords',
        access: 'canViewProductTransferRecord',
      },
      {
        name: 'warehouse-transfer-exception-followup',
        path: '/warehouse-transfer/exception-followup',
        component: './WarehouseTransferManagement/ExceptionFollowup',
        access: 'canViewTransferExceptionFollowup',
      },
      {
        name: 'warehouse-transfer-product-search',
        path: '/warehouse-transfer/product-search',
        component: './WarehouseTransferManagement/TransferProductSearch',
        access: 'canViewTransferProductSearch',
      },
      {
        name: 'warehouse-transfer-picking-progress',
        path: '/warehouse-transfer/picking-progress',
        component: './WarehouseTransferManagement/PickingProgress',
        access: 'canViewTransferPickingProgress',
      },
      {
        name: 'warehouse-transfer-shipping-schedule',
        path: '/warehouse-transfer/shipping-schedule',
        component: './WarehouseTransferManagement/ShippingSchedule',
        access: 'canViewTransferShippingSchedule',
      },
      {
        name: 'warehouse-transfer-arrival-schedule',
        path: '/warehouse-transfer/arrival-schedule',
        component: './InventoryManagement/ArrivalCalendar',
        access: 'canViewPurchaseOrders',
      },
      {
        name: 'warehouse-purchase-order-arrival-schedule',
        path: '/warehouse-transfer/purchase-order-arrival-schedule',
        component: './WarehouseTransferManagement/WarehousePurchaseOrderArrivalSchedule',
        access: 'canViewWarehousePurchaseOrderArrivalSchedule',
      },
      {
        name: 'warehouse-transfer-store-return',
        path: '/warehouse-transfer/store-returns',
        component: './WarehouseTransferManagement/StoreReturnManagement',
        access: 'canViewStoreReturn',
      },
      {
        name: 'purchase-order-receipt-confirmation',
        path: '/warehouse-transfer/purchase-order-receipts',
        component: './WarehouseTransferManagement/PurchaseOrderReceiptConfirmation',
        access: 'canViewPurchaseReceiptConfirmations',
      },
      {
        name: 'warehouse-transfer-detail',
        path: '/warehouse-transfer/detail/:id',
        component: './WarehouseTransferManagement/TransferDetail',
        hideInMenu: true,
        access: 'canViewWarehouseTransferDetail',
      },
      {
        name: 'warehouse-transfer-create',
        path: '/warehouse-transfer/create',
        component: './WarehouseTransferManagement/CreateTransfer',
        hideInMenu: true,
        access: 'canCreateEditTransferOrder',
      },
      {
        name: 'warehouse-transfer-edit',
        path: '/warehouse-transfer/edit/:id',
        component: './WarehouseTransferManagement/EditTransfer',
        hideInMenu: true,
        access: 'canCreateEditTransferOrder',
      },
      {
        name: 'warehouse-transfer-manual-receiving',
        path: '/warehouse-transfer/manual-receiving/:id',
        component: './WarehouseTransferManagement/ManualReceiving',
        hideInMenu: true,
        access: 'canManualReceiveTransferOrder',
      },
      {
        name: 'inspection-records',
        path: '/warehouse-transfer/inspection-records',
        component: './WarehouseTransferManagement/InspectionRecords',
        access: 'canViewInspectionRecord',
      },
      {
        name: 'warehouse-transfer-products',
        path: '/warehouse-transfer/products/:id',
        component: './WarehouseTransferManagement/ProductSelection',
        hideInMenu: true,
        access: 'canViewTransferOrder',
      },
    ],
  },
  {
    name: 'member-management',
    icon: 'team',
    path: '/member-management',
    access: 'memberManagementModule',
    routes: [
      {
        path: '/member-management',
        redirect: '/member-management/members',
      },
      {
        name: 'members',
        icon: 'user',
        path: '/member-management/members',
        access: 'canViewMembers',
        routes: [
          {
            path: '/member-management/members',
            component: './MemberManagement',
            exact: true,
          },
          {
            path: '/member-management/members/:id',
            component: './MemberManagement/MemberDetail',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'coupon-templates',
        icon: 'gift',
        path: '/member-management/coupon-templates',
        access: 'canViewCouponTemplate',
        routes: [
          {
            path: '/member-management/coupon-templates',
            component: './MemberManagement/CouponTemplates',
            exact: true,
          },
          {
            path: '/member-management/coupon-templates/:id',
            component: './MemberManagement/CouponTemplates/Detail',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'coupons',
        icon: 'tag',
        path: '/member-management/coupons',
        component: './MemberManagement/Coupons',
        access: 'canViewMemberCoupon',
      },
    ],
  },
  {
    name: 'finance',
    icon: 'bank',
    path: '/finance',
    access: 'financeModule',
    routes: [
      { path: '/finance', redirect: '/finance/overview' },
      {
        name: 'overview',
        icon: 'dashboard',
        path: '/finance/overview',
        component: './Finance/Overview',
        access: 'canViewFinanceOverview',
      },
      {
        name: 'setup',
        icon: 'safetyCertificate',
        path: '/finance/setup',
        component: './Finance/Setup',
        access: 'canViewFinanceSetup',
      },
      {
        name: 'banking',
        icon: 'transaction',
        path: '/finance/banking',
        component: './Finance/Banking',
        access: 'canViewFinanceBanking',
      },
      {
        name: 'bank-sync',
        icon: 'sync',
        path: '/finance/bank-sync',
        component: './Finance/BankSync',
        access: 'canViewFinanceBankSync',
      },
      {
        name: 'chart-of-accounts',
        icon: 'apartment',
        path: '/finance/chart-of-accounts',
        component: './Finance/ChartOfAccounts',
        access: 'canViewFinanceCoa',
      },
      {
        name: 'reconciliation',
        icon: 'audit',
        path: '/finance/reconciliation',
        component: './Finance/Reconciliation',
        access: 'canViewFinanceReconciliation',
      },
      {
        name: 'journals',
        icon: 'book',
        path: '/finance/journals',
        component: './Finance/Journals',
        access: 'canViewFinanceJournals',
      },
      {
        name: 'reports',
        icon: 'barChart',
        path: '/finance/reports',
        component: './Finance/Reports',
        access: 'canViewFinanceReports',
      },
      {
        path: '/finance/rent/payments/new',
        component: './Finance/Rent/PaymentNew',
        access: 'canViewStoreRent',
      },
      {
        name: 'rent',
        icon: 'payCircle',
        path: '/finance/rent',
        component: './Finance/Rent',
        access: 'canViewStoreRent',
      },
      {
        name: 'audit-trail',
        icon: 'safetyCertificate',
        path: '/finance/audit',
        component: './Finance/Audit',
        access: 'canViewFinanceAudit',
      },
      {
        name: 'operational-finance',
        icon: 'deploymentUnit',
        path: '/finance/operational',
        component: './Finance/Operational',
        access: 'canViewFinanceReports',
      },
    ],
  },
  {
    name: 'user-management',
    icon: 'user',
    path: '/user-management',
    access: 'userManagementModule',
    routes: [
      {
        path: '/user-management',
        redirect: '/user-management/users',
      },
      {
        name: 'users',
        path: '/user-management/users',
        component: './UserManagement',
        access: 'canViewUsers',
      },
      {
        name: 'teams',
        icon: 'team',
        path: '/user-management/team',
        access: 'canViewTeams',
        routes: [
          {
            path: '/user-management/team',
            component: './TeamManagement',
            exact: true,
          },
          {
            path: '/user-management/team/:id',
            component: './TeamManagement/TeamDetail',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'roles',
        icon: 'usergroup-add',
        path: '/user-management/roles',
        access: 'canViewRoles',
        routes: [
          {
            path: '/user-management/roles',
            component: './RoleManagement',
            exact: true,
          },
          {
            path: '/user-management/roles/:id/permissions',
            component: './RoleManagement/PermissionSettings',
            hideInMenu: true,
          },
        ],
      },
      {
        name: 'franchisee-accounts',
        icon: 'team',
        path: '/user-management/franchisee-accounts',
        component: './FranchiseeAccountManagement',
        access: 'franchiseeManagementModule',
      },
      {
        name: 'warehouse-users',
        icon: 'team',
        path: '/user-management/warehouse-users',
        component: './WarehouseUserManagement',
        access: 'canViewWarehouseUsers',
      },
      {
        name: 'wholesale-customers',
        icon: 'shop',
        path: '/user-management/wholesale-customers',
        component: './WholesaleCustomerManagement',
        access: 'canViewWholesaleCustomers',
      },
      {
        name: 'fgshopUserManagement',
        icon: 'mobile',
        path: '/user-management/fgshop-users',
        component: './FgshopUserManagement',
        access: 'canManageMobileUsers',
      },
      {
        name: 'notification-logs',
        icon: 'bell',
        path: '/user-management/notification-logs',
        component: './NotificationLog',
        access: 'canViewNotificationLogs',
      },
      {
        name: 'product-view-logs',
        icon: 'profile',
        path: '/user-management/product-view-logs',
        component: './UserProductViewLogs',
        access: 'canViewProductViewLogs',
      },
    ],
  },
  {
    path: '/franchisee-management',
    redirect: '/user-management/franchisee-accounts',
  },
  {
    path: '/franchisee-management/accounts',
    redirect: '/user-management/franchisee-accounts',
  },
  {
    path: '/users',
    redirect: '/user-management/users',
  },
  {
    path: '/analytics',
    redirect: '/sales-analysis/dashboard',
  },
  {
    path: '/franchise',
    layout: false,
    routes: [
      {
        name: 'franchise-login',
        path: '/franchise/login',
        component: './Franchise/Login',
      },
      {
        path: '/franchise',
        redirect: '/franchise/sales-analysis/dashboard',
      },
      {
        path: '/franchise/sales-analysis/dashboard',
        component: './Franchise/ProtectedSalesAnalysis',
      },
    ],
  },
  {
    path: '/scripts',
    redirect: '/script-management/scripts',
  },
  {
    path: '/script-runs',
    redirect: '/script-management/script-runs',
  },
  {
    path: '/discounts',
    redirect: '/product-management/discounts',
  },
  {
    path: '/product-management/online-products',
    redirect: '/online-store-management/products',
  },
  {
    path: '/orders/online',
    redirect: '/online-store-management/orders',
  },
  {
    path: '/sales-analysis/online-store-behaviour',
    redirect: '/online-store-management/analytics',
  },
  {
    path: '/online-store-management/marketing-management',
    redirect: '/marketing-management/dashboard',
  },
  {
    path: '/online-store-management/marketing-management/dashboard',
    redirect: '/marketing-management/dashboard',
  },
  {
    path: '/pos-system-management/online-store-management',
    redirect: '/online-store-management',
  },
  {
    path: '/pos-system-management/online-store-management/products',
    redirect: '/online-store-management/products',
  },
  {
    path: '/pos-system-management/online-store-management/orders',
    redirect: '/online-store-management/orders',
  },
  {
    path: '/pos-system-management/online-store-management/analytics',
    redirect: '/online-store-management/analytics',
  },
  {
    path: '/pos-system-management/online-store-management/email-notification-tasks',
    redirect: '/online-store-management/email-notification-tasks',
  },
  {
    path: '/payment-gateways',
    redirect: '/pos-system-management/payment-gateways',
  },
  {
    path: '/location-accounts',
    redirect: '/pos-system-management/location-accounts',
  },
  {
    path: '/locations',
    redirect: '/store-management/locations',
  },
  {
    path: '/category-placement',
    redirect: '/store-management/category-placement',
  },
  {
    path: '/store-management/bea-config-management',
    redirect: '/pos-system-management/bea-config-management',
  },
  {
    path: '/duplicate-barcodes',
    redirect: '/data-anomaly/duplicate-barcodes',
  },
  {
    path: '/inventory',
    redirect: '/inventory-management/overview',
  },
  {
    path: '/mi/mobile/user/login',
    layout: false,
    component: './mi/mobile/user/login',
  },
  {
    path: '/mi/mobile/mi-tasks',
    layout: false,
    component: './mi/mobile/mi-tasks',
  },
  {
    path: '/mi/mobile/mi-tasks/:id',
    layout: false,
    component: './mi/mobile/mi-tasks/[id]',
  },
  {
    path: '/mi/mobile/tasks/:taskId/capture',
    layout: false,
    component: './mi/mobile/tasks/capture',
  },
  {
    path: '/mi/mobile/tasks/:taskId/history',
    layout: false,
    component: './mi/mobile/tasks/history',
  },
  {
    path: '/products',
    redirect: '/product-management/products',
  },
  {
    path: '/products/:id',
    redirect: '/product-management/products/:id',
  },
  {
    path: '/purchase-order-opportunity',
    redirect: '/purchase-order-management/opportunities',
  },
  {
    path: '/purchase-order-opportunity/opportunities',
    redirect: '/purchase-order-management/opportunities',
  },
  {
    path: '/purchase-order-opportunity/opportunities/:id',
    redirect: '/purchase-order-management/opportunities/:id',
  },
  {
    path: '/purchase-offer',
    redirect: '/purchase-order-management/offers',
  },
  {
    path: '/purchase-offer/offers',
    redirect: '/purchase-order-management/offers',
  },
  {
    path: '/purchase-offer/offers/:id',
    redirect: '/purchase-order-management/offers/:id',
  },
  {
    path: '/suppliers',
    redirect: '/supplier-management/suppliers',
  },
  {
    path: '/debug-permissions',
    component: './DebugPermissions',
    access: 'canViewRoles',
    hideInMenu: true,
  },
  {
    path: '*',
    layout: false,
    component: './404',
  },
];
